// @greenlight-spec: 28-it-dashboard-ui.md

import * as React from 'react';

/**
 * Runs one async action against the entry that was on screen when it started.
 *
 * The Assets drawer swaps entries without unmounting, so every request here can
 * outlive the entry it belongs to. Three things follow from that, and each was
 * found missing from a different action before this existed:
 *
 * - work on the **cache** belongs to the entry the request targeted, and must
 *   happen whatever is on screen when it returns;
 * - **feedback** — toasts, closing a form, clearing fields — is owed only to the
 *   entry still in front of the user;
 * - **pending** is per-entry, or one entry's request disables another's controls.
 *
 * Call it once per action so each keeps its own pending flag.
 */
export interface EntryScopedAction<T> {
  /** True only while a request started against the entry on screen is running. */
  pending: boolean;
  run: (opts: {
    perform: () => Promise<T>;
    /** Cache work. Runs on success regardless of which entry is on screen. */
    settle?: (result: T, targetEntryId: string) => void;
    /** Feedback. Runs only if the drawer is still on the entry it started against. */
    report?: (result: T) => void;
    /** Feedback for a thrown failure, under the same visibility rule. */
    reportError?: (message: string) => void;
  }) => Promise<void>;
}

export function useEntryScopedAction<T>(entryId: string): EntryScopedAction<T> {
  const current = React.useRef(entryId);
  current.current = entryId;
  const [pendingFor, setPendingFor] = React.useState<string | null>(null);

  const run = React.useCallback(
    async (opts: {
      perform: () => Promise<T>;
      settle?: (result: T, targetEntryId: string) => void;
      report?: (result: T) => void;
      reportError?: (message: string) => void;
    }): Promise<void> => {
      const target = current.current;
      setPendingFor(target);
      try {
        const result = await opts.perform();
        opts.settle?.(result, target);
        if (current.current !== target) return;
        opts.report?.(result);
      } catch (err) {
        if (current.current !== target) return;
        opts.reportError?.(err instanceof Error ? err.message : 'The request failed.');
      } finally {
        setPendingFor((p) => (p === target ? null : p));
      }
    },
    [],
  );

  return { pending: pendingFor === entryId, run };
}
